import React, { useState } from 'react';
import type { SignupRequest, SocialProvider } from '../domain/account';
import { toSignupRequest, type SignUpValues } from '../utils/account';
import { DomainError, isDomainError } from '../domain/errors';
import { useRepositories } from '../hooks/useRepositories';
import { ActionError } from './PageStates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ErrorText } from './ErrorText';
import { PRIVACY_URL, TERMS_URL } from '../domain/legal';

type Field = keyof SignUpValues;

const FIELDS: {
  name: Field;
  label: string;
  type: string;
  autoComplete: string;
  required: boolean;
  hint?: string;
}[] = [
  {
    name: 'firstName',
    label: 'First name',
    type: 'text',
    autoComplete: 'given-name',
    required: true,
  },
  {
    name: 'lastName',
    label: 'Last name',
    type: 'text',
    autoComplete: 'family-name',
    required: true,
  },
  { name: 'username', label: 'Username', type: 'text', autoComplete: 'username', required: true },
  {
    name: 'password',
    label: 'Password',
    type: 'password',
    autoComplete: 'new-password',
    required: true,
  },
  {
    name: 'contact',
    label: 'Email or phone number',
    type: 'text',
    autoComplete: 'email',
    required: false,
    hint: 'Optional. You can invite, post and message once you verify an email or phone.',
  },
];

const BLANK: Partial<Record<Field, string>> = {
  firstName: 'Enter your first name.',
  lastName: 'Enter your last name.',
  username: 'Choose a username.',
  password: 'Choose a password.',
};

const PROVIDERS: { provider: SocialProvider; label: string }[] = [
  { provider: 'apple', label: 'Continue with Apple' },
  { provider: 'google', label: 'Continue with Google' },
];

/**
 * Making an account, the same on every client (019 R1, R15): names,
 * username, password and an optional email or phone, or Apple or Google.
 * A blank name is refused beside its field before anything is sent; the
 * server's field errors land beside their fields too, and a refusal of the
 * terms lands under the notice that every way in here agrees to them.
 */
export default function SignUpForm({
  onSubmit,
  next,
}: {
  /** Send the request; reject with a `DomainError` to show it. */
  onSubmit: (request: SignupRequest) => Promise<void>;
  /** Where Apple and Google return to. */
  next: string;
}) {
  const { session } = useRepositories();
  const [values, setValues] = useState<SignUpValues>({
    firstName: '',
    lastName: '',
    username: '',
    password: '',
    contact: '',
  });
  const [blank, setBlank] = useState<Partial<Record<Field, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<DomainError | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const missing = Object.fromEntries(
      FIELDS.filter((f) => f.required && !values[f.name].trim()).map((f) => [
        f.name,
        BLANK[f.name],
      ]),
    );
    setBlank(missing);
    if (Object.keys(missing).length) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(toSignupRequest(values));
    } catch (err) {
      setError(isDomainError(err) ? err : new DomainError('failure', 'Something went wrong.'));
      setSubmitting(false);
    }
  };

  const serverFields = error?.kind === 'invalid' ? error.fields : {};
  const fieldErrors = (f: Field): string[] => {
    const own = blank[f] ? [blank[f]!] : [];
    const server =
      f === 'contact'
        ? [...(serverFields.email ?? []), ...(serverFields.phone ?? [])]
        : (serverFields[f] ?? []);
    return [...own, ...server];
  };
  const termsErrors = [...(serverFields.acceptedTerms ?? []), ...(serverFields.termsVersion ?? [])];
  const placed = termsErrors.length > 0 || FIELDS.some((f) => fieldErrors(f.name).length > 0);
  const unplaced = error && !(error.kind === 'invalid' && placed) ? error : null;

  return (
    <div className="space-y-6">
      <form onSubmit={submit} noValidate className="space-y-4">
        {FIELDS.map((f) => {
          const id = `signup-${f.name}`;
          const messages = fieldErrors(f.name);
          const describedBy = [messages.length ? `${id}-error` : '', f.hint ? `${id}-hint` : '']
            .filter(Boolean)
            .join(' ');
          return (
            <div key={f.name}>
              <Label htmlFor={id}>{f.label}</Label>
              <Input
                id={id}
                name={f.name}
                type={f.type}
                autoComplete={f.autoComplete}
                required={f.required}
                value={values[f.name]}
                onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
                aria-invalid={messages.length > 0 || undefined}
                aria-describedby={describedBy || undefined}
                className="mt-1"
              />
              {f.hint && (
                <p id={`${id}-hint`} className="mt-1 text-xs text-muted-foreground">
                  {f.hint}
                </p>
              )}
              {messages.length > 0 && (
                <ErrorText id={`${id}-error`} className="mt-1 text-sm">
                  {messages.join(' ')}
                </ErrorText>
              )}
            </div>
          );
        })}

        {unplaced && <ActionError error={unplaced} />}

        <Button type="submit" className="w-full rounded-full py-6 font-bold" disabled={submitting}>
          {submitting ? 'Creating account…' : 'Create account'}
        </Button>
      </form>

      <div>
        <p id="signup-terms" className="text-center text-sm leading-relaxed text-muted-foreground">
          By continuing, you agree to our <TermsLink href={TERMS_URL}>Terms of Use</TermsLink> and{' '}
          <TermsLink href={PRIVACY_URL}>Privacy Policy</TermsLink>, and confirm you’re 18 or older.
        </p>
        {termsErrors.length > 0 && (
          <ErrorText id="signup-terms-error" role="alert" className="mt-1 justify-center text-sm">
            {termsErrors.join(' ')}
          </ErrorText>
        )}
      </div>

      <div className="space-y-2">
        <p className="text-center text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Or
        </p>
        {PROVIDERS.map(({ provider, label }) => (
          <Button key={provider} variant="secondary" className="w-full rounded-full" asChild>
            <a href={session.socialSignInUrl(provider, next)}>{label}</a>
          </Button>
        ))}
      </div>
    </div>
  );
}

/** A legal page, opened beside the form so nothing typed is lost. */
function TermsLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener"
      className="font-semibold text-primary underline underline-offset-2"
    >
      {children}
    </a>
  );
}
